import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  Alert,
} from '@mui/material';
import { IoClose } from 'react-icons/io5';

interface EditClassModalProps {
  open: boolean;
  onClose: () => void;
  classItem: { id: string; name?: string; title?: string } | null;
  onSubmit: (id: string, newTitle: string) => Promise<void>;
  isSubmitting?: boolean;
}

export const EditClassModal: React.FC<EditClassModalProps> = ({
  open,
  onClose,
  classItem,
  onSubmit,
  isSubmitting = false,
}) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (classItem) {
      setTitle(classItem.name || classItem.title || '');
      setError(null);
    }
  }, [classItem, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Grade / Class title is required.');
      return;
    }

    if (!classItem) return;

    try {
      setError(null);
      await onSubmit(classItem.id, title.trim());
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to update Grade Level.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" fontSize="1.125rem">
            Rename Grade Level / Class
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Update the title for this academic grade container
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ py: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={2}>
            <TextField
              label="Grade / Class Title"
              placeholder="e.g. Grade 6, Grade 7, Grade 8, HS Algebra"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
              InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.875rem' } }}
              helperText="All nested cohorts will retain their relationship under this renamed grade."
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !title.trim()}
            sx={{
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              px: 2.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
